import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { toast } from "sonner";
import { trackOperation } from "@/app/live";
import { isApiError } from "@/lib/api/errors";
import { apiKey, useApiMutation } from "@/lib/api/hooks";
import { config } from "@/lib/config";
import { useT } from "@/lib/i18n";
import { isNoOrganization, noOrgToast } from "@/lib/reauth";
import type { ChannelAccount } from "@/lib/types";

const refresh = (qc: QueryClient, ...names: string[]) => Promise.all(names.map((n) => qc.invalidateQueries({ queryKey: [n] })));

/** Why connecting failed: the API problem's `detail`, else its `title`, else the generic line (ALP-77). */
export function connectErrorText(e: unknown, fallback: string): string {
  if (!isApiError(e)) return fallback;
  return e.problem?.detail?.trim() || e.problem?.title?.trim() || fallback;
}

/**
 * Start Instagram Login (ALP-77). The API answers `{ redirectUrl }` = its `/v1/oauth/instagram/start` URL; the browser goes
 * there (→ instagram.com → our callback → back to `/settings/channels?connected=…`). In mock mode the mock "does" the round
 * trip and answers a same-origin return URL, followed with client-side navigation so the in-browser mock data survives.
 * A token connect (dev) returns the account itself.
 */
export function useConnectInstagram(onConnected?: (a: ChannelAccount) => void) {
  const { t } = useT();
  const qc = useQueryClient();
  const router = useRouter();
  return useApiMutation("connectAccount", {
    onSuccess: async (res) => {
      if ("redirectUrl" in res) {
        const target = new URL(res.redirectUrl, window.location.href);
        if (config().mock && target.origin === window.location.origin) {
          await refresh(qc, "accounts", "jobs", "jobCounts");
          await router.navigate({ href: `${target.pathname}${target.search}` });
          return;
        }
        toast.loading(t("onboarding.connect.redirecting"));
        window.location.assign(res.redirectUrl);
        return;
      }
      await refresh(qc, "accounts", "inbox", "inboxCounts");
      toast.success(t("settings.channels.connectedToast"));
      onConnected?.(res);
    },
    // the API problem says why (detail = readable text, title = the code); only fall back to the generic line
    onError: (e) => (isNoOrganization(e) ? noOrgToast() : toast.error(connectErrorText(e, t("onboarding.connect.failed")))),
  });
}

function patchAccount(qc: QueryClient, id: string, patch: Partial<ChannelAccount>) {
  qc.setQueryData<ChannelAccount[]>(apiKey("accounts"), (old) => old?.map((a) => (a.id === id ? { ...a, ...patch } : a)));
}

/** Toggle AI for an account, optimistically (rolled back on failure). */
export function useToggleAi() {
  const { t } = useT();
  const qc = useQueryClient();
  return useApiMutation<"updateAccount", { prev: ChannelAccount[] | undefined }>("updateAccount", {
    onMutate: async ({ params, body }) => {
      await qc.cancelQueries({ queryKey: ["accounts"] });
      const prev = qc.getQueryData<ChannelAccount[]>(apiKey("accounts"));
      if (params?.accountId && body.aiEnabled !== undefined) patchAccount(qc, params.accountId, { aiEnabled: body.aiEnabled });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx) qc.setQueryData(apiKey("accounts"), ctx.prev);
      toast.error(t("common.states.error"));
    },
    onSuccess: (a) => {
      patchAccount(qc, a.id, a);
      toast.success(a.aiEnabled ? t("settings.channels.aiOn", { user: a.username }) : t("settings.channels.aiOff", { user: a.username }));
    },
  });
}

export function useSyncAccount() {
  const { t } = useT();
  const qc = useQueryClient();
  return useApiMutation("syncAccount", {
    onMutate: ({ params }) => {
      if (params?.accountId) patchAccount(qc, params.accountId, { status: "syncing" });
    },
    onSuccess: (op, { params }) => {
      const acc = qc.getQueryData<ChannelAccount[]>(apiKey("accounts"))?.find((a) => a.id === params?.accountId);
      const user = acc?.username ?? "";
      trackOperation(op.id, { label: t("settings.channels.syncing", { user }), done: t("settings.channels.synced", { user }), onDone: () => void refresh(qc, "accounts") });
      // the operation may finish while the live stream is down: refresh anyway
      setTimeout(() => void refresh(qc, "accounts"), 4000);
    },
    onError: () => {
      void refresh(qc, "accounts");
      toast.error(t("common.states.error"));
    },
  });
}

export { refresh as refreshQueries };
